/* globals k */

import React, { Component } from 'react';
import classNames from 'classnames';

import 'froala-editor/js/froala_editor.min.js'; 

import { DEFAULT_FROALA_TOOLBAR_BUTTONS } from './froala_editor_buttons.js';
import { ALLOWED_HTML_TAGS, ALLOWED_HTML_ATTRS } from 'const_shared';

// See plugins list: https://www.froala.com/wysiwyg-editor/docs/plugins
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/line_breaker.min.js'; // Better inserting of lines after elements like tables
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/js/plugins/word_paste.min.js'; // Clean content pasted from MS word
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/help.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/third_party/font_awesome.min.js';

import 'froala-editor/css/plugins/help.min.css';
import 'froala-editor/css/plugins/line_breaker.css';
import 'froala-editor/css/plugins/code_view.css';
import 'froala-editor/css/plugins/table.css';
import 'font-awesome/css/font-awesome.min.css';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';

import FroalaEditorComponent from 'react-froala-wysiwyg';


const FROALA_KEY = k.FROALA_ACTIVATION_KEY;

export const PRIMARY_BUTTON_STYLE_CLASSNAME = 'button-style';
export const SECONDARY_BUTTON_STYLE_CLASSNAME = 'secondary-button-style';


const HELP_SETS = [
  {
    title: 'Common actions',
    commands: [
      { val: 'OSkeyC',  desc: 'Copy' },
      { val: 'OSkeyX',  desc: 'Cut' },
      { val: 'OSkeyV',  desc: 'Paste' },
      { val: 'OSkeyZ',  desc: 'Undo' },
      { val: 'OSkeyShift+Z',  desc: 'Redo' },
    ]
  },
  {
    title: 'Basic Formatting',
    commands: [
      { val: 'OSkeyA',  desc: 'Select All' },
      { val: 'OSkeyB',  desc: 'Bold' },
      { val: 'OSkeyI',  desc: 'Italic' },
      { val: 'OSkeyU',  desc: 'Underline' },
      { val: 'OSkeyS',  desc: 'Strikethrough' },
      { val: 'OSkey]',  desc: 'Increase Indent' },
      { val: 'OSkey[',  desc: 'Decrease Indent' }
    ]
  },
  {
    title: 'Quote',
    commands: [
      { val: 'OSkey\'',  desc: 'Increase quote level' },
      { val: 'OSkeyShift+\'',  desc: 'Decrease quote level' }
    ]
  },
  {
    title: 'Table',
    commands: [
      { val: 'Alt+Space',  desc: 'Select table cell' },
      { val: 'Shift+Left/Right arrow',  desc: 'Extend selection one cell' },
      { val: 'Shift+Up/Down arrow',  desc: 'Extend selection one row' }
    ]
  },
  {
    title: 'Navigation',
    commands: [
      { val: 'OSkey/',  desc: 'Shortcuts' },
      { val: 'Alt+F10',  desc: 'Focus popup / toolbar' },
      { val: 'Esc',  desc: 'Return focus to previous position' }
    ]
  }
];


class CustomizedFroalaEditor extends Component {

/*
  This function solves a very tricky issue with Froala and our back-end. In our back-end we clean
  all the HTML with mozilla/bleach (https://github.com/mozilla/bleach) and in the  front-end the
  HTML is cleaned by Froala clean (https://froala.com/wysiwyg-editor/docs/methods/#clean). These
  two functions clean the HTML slightly differently, leading to a cursor issue in the Froala
  editor. Basically what happens is:

  1. The user edits the HTML
  2. The HTML is sent to the back-end
  3. The back-end cleans the HTML and sends it back
  4. Froala receives the HTML cleaned by the back-end in the model prop
  5. If the back-end clean is different from what was received from Froala the model prop value
     has changed leading to Froala assuming that the entire HTML has been changed programmatically
     and resets the cursor position.
  6. Froala then cleans the HTML again, meaning that the next request to the back-end will be
     cleaned yet again and the issue repeats ad infinitum.

  This is incredibly frustrating to users since everytime they type something in the editor the
  cursor jumps to the first character for "no reason".

  This only happens when the Froala clean is different from the back-end clean, which most commonly
  is caused by symbols like "&" and "&nbsp;". For example, if "&" exists in the HTML the back-end
  would receive "&" from Froala and then proceed to replace it with "&amp;". Froala would then
  receive "&amp;" and replace it with "&". This will repeat for every edit for as long as "&"
  exists in the HTML.

  We solve this issue by manually cleaning the model before it is sent in as a prop. This way
  Froala does not register a change in the HTML and the cursor position is preserved.
*/
  froalaCleanModel(model){
    if(!model){
      // this.froalaEditor.clean.html throws an error when given a null value
      return model;
    }

    return this.froalaEditor ? this.froalaEditor.clean.html(model) : model;
  }

  render() {
    const {
      className,
      config,
      model
    } = this.props;

    const _this = this;
    const events = config.events || {};

    return (
      <div 
        className={classNames('froala-zf-container', className)} 
        ref={(node)=> { this.nodeRef = node; }}>

        <FroalaEditorComponent {...{
          ...this.props,
          model: this.froalaCleanModel(model),
          tag: 'textarea',
          config: {
            ...config,
            key: FROALA_KEY,
            toolbarButtons: (
              config.toolbarButtons ?
                {
                  ...DEFAULT_FROALA_TOOLBAR_BUTTONS,
                  ...config.toolbarButtons
                }
              :
                DEFAULT_FROALA_TOOLBAR_BUTTONS
            ),
            attribution: false,
            paragraphDefaultSelection: 'P',
            helpSets: HELP_SETS,
            htmlAllowedTags: ALLOWED_HTML_TAGS,
            htmlAllowedAttrs: ALLOWED_HTML_ATTRS,
            codeBeautifierOptions: {
              end_with_newline: false,
              indent_inner_html: true,
              extra_liners: '["p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "ul", "ol", "table", "dl"]',
              brace_style: 'expand',
              indent_char: '\ ',
              indent_size: 2,
              wrap_line_length: 0
            },
            events: {
              ...events,
              'link.beforeInsert': forceTargetToParentForNonBlankHack,
              'initialized': events['initialized'] ?
                function() {
                  _this.froalaEditor = this;
                  config.events['initialized'](this);
                }
              :
                function() {
                  _this.froalaEditor = this;
                }
              
            }
          }
        }}
        />

      </div>
    );
  }
}

/*
  Set any non-`"_blank"`` target to `"_parent"`.

  NOTE: The froala links plugin seems to only be able to understand 
  `_blank` and `_parent` tags, and so elects to add a `target="_blank"` 
  whenever it enounters an `<a>` tag with no value or a blank value. This 
  behavior certainly seems like a bug, but that is unclear. This behavior
  is known to zoomforth to exists in `froala/wysiwyg-editor` v2.7.5 - v2.9.1
*/
export const forceTargetToParentForNonBlankHack = (link, text, attrs)=> {
  if (attrs.target !== '_blank') {
    attrs.target = '_parent';
  }
};


export const focusAtEnd = (editor) => {
  editor.events.focus();
  editor.selection.setAtEnd(editor.$el.get(0));
  editor.selection.restore();
};

export const getLinkStyleOptions = () => ({
  [PRIMARY_BUTTON_STYLE_CLASSNAME]: 'Primary Button',
  [SECONDARY_BUTTON_STYLE_CLASSNAME]: 'Secondary Button',
  '': 'Link' // we set no class on a normal link to reset it
});

/*
  When the "align left" command is executed froala just un-sets any alignment in CSS
  for the block. However there are times when we must force the editor to apply
  the left-align to the block to override some other style setting.
*/
export const forceSetLeftAlignOnLeftAlignCmd = (e, editor, cmd, align)=> {
  if (cmd === 'align' && align === 'left') {
    const blocks = editor.selection.blocks();
    if (blocks.length > 0) {
      blocks[0].style.textAlign = 'left';
    }
  }
};


export default CustomizedFroalaEditor;
